/** @file
 * @brief Localization module for the application, utilizing i18next for internationalization support.
 * @details This module initializes i18next with custom backend loading logic to fetch localization resources.
 * It supports dynamic loading of language resources and provides a global `g_lang` object for accessing translation functions.
 * The module also includes polyfills for internationalization features to ensure compatibility with older browsers like IE11.
 * The localization resources are expected to be defined in a separate `locales` module, and the current language is determined by a global variable `rvLanguage`.
 * The module exports the `g_lang` object for use throughout the application, as well as React hooks for translation.
 */

import 'intl-pluralrules'; // TODO: remove polyfill when IE11 support is dropped

import { rvLanguage } from './globals';
import type { BackendModule, CallbackError, i18n, InitOptions, ReadCallback } from 'i18next';
import i18next from 'i18next';
import i18nextModuleICU from 'i18next-icu';
import { initReactI18next as i18nextModuleReact } from 'react-i18next';

import localeResources from '../locales'; // en, ru

type AllowedResources = typeof localeResources["en"] | typeof localeResources["ru"];
type AllowedTKeys = keyof AllowedResources["translation"];

const i18nextOptions: InitOptions = {
  debug: false,                //debug: true,
  lng: rvLanguage,             //lng: rvLanguage || "ru",
  supportedLngs: [rvLanguage], //supportedLngs: ["ru", "en"],
  preload: [rvLanguage],       //preload: ["ru", "en"],
  fallbackLng: rvLanguage,     //fallbackLng: "en",
  initImmediate: false,
  interpolation: {
    escapeValue: false,
  },
  // don't set resources because otherwise i18next won't be initialized synchronously
};

function isStringRecord(x: unknown): x is { [x: string]: string } {
  if (typeof x !== "object") {
    return false;
  }

  const keys = Object.keys(x);
  const isValid = keys.every(key => {
    // @ts-ignore
    const t = typeof x[key];
    return t === "string" || t === "undefined";
  });

  return isValid;
};

// inspired by "i18next-resources-to-backend"
const resourcesBackendModule: BackendModule = {
  type: "backend",
  init() : void {
    // do nothing
  },
  read(language:string, namespace:string, callback:ReadCallback) : void {
    try {
      let data: { [x: string]: string } = {};

      // for compatibility try to load the data from global g_lang
      // @ts-ignore
      const windowStrings = window["g_lang"];
      if (isStringRecord(windowStrings)) {
        data = windowStrings;
      }

      // @ts-ignore
      const resourceStrings = localeResources[language][namespace];
      if (resourceStrings && isStringRecord(resourceStrings))
        Object.assign(data, resourceStrings);
      else
        console.warn(`[i18next] no valid resources for ${language} (namespace ${namespace})`, resourceStrings);

      callback(null, data);
    } catch (err) {
      callback(err as CallbackError, false); // no retry
    }
  }
};

i18next
  .use(resourcesBackendModule)
  .use(i18nextModuleICU)
  .use(i18nextModuleReact)
  .init(i18nextOptions, function (err) : void {
    if (err)
      console.error('[i18next] Error loading localization', err);
  });

let g_lang: { [key in AllowedTKeys]: string } & i18n;

// @ts-ignore
g_lang = i18next;

export default g_lang;

export { useTranslation, Trans } from 'react-i18next';